import React, { Suspense, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import { AnimatePresence } from 'framer-motion';
import SystemCheck from 'common/SystemCheck';
import { AppRoutePath } from 'config/constants';
import {
  LoadingScreenContext,
  useLoadingScreen,
  usePostLoadState,
} from 'domains/worklist/Worklist/hooks/useLoadingScreen';
import useAuth, { AuthProvider, useMonitorAuthToken } from 'hooks/useAuth';
import { isAuthlessPathname, isAuthlessStaticPathname, isLogoutPathname } from 'utils/router';
import { Navbar } from '../../Navbar/Navbar';
import WorkspaceManager from '../../WorkspaceManager/WorkspaceManager';
import { useFocusManager } from 'modules/focusManager';
import { useWindowStateAnalytics } from 'modules/windowStateAnalytics';
import { useSystemInfoAnalytics } from 'modules/systemInfoAnalytics';
import { useTrackWindowRect } from 'hooks/useWindowsRects';
import { Background as LoginBackground } from '../Auth/Background';
import { PWATitleBar } from '../../ui/PWATitleBar';
import { SIDENAV_WIDTH } from 'domains/reporter/RichTextEditor/plugins/picklist/constants';
import { useSidebarVisible } from 'domains/viewer/Viewer/viewerState';
import { useCheckUserRISPermissions } from 'integrations/RIS/checkPermissions';
import { telemetryContextFor } from 'modules/Apollo/types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';

const WorkspaceManagerWrapper = () => {
  const [isWorkspaceLayoutV2Enabled] = useFeatureFlagEnabled(FF.WORKSPACE_LAYOUT_V2);

  return isWorkspaceLayoutV2Enabled ? null : <WorkspaceManager />;
};

export default function BackgroundRoute(): React.ReactElement {
  useFocusManager();
  useTrackWindowRect();
  useWindowStateAnalytics();
  useSystemInfoAnalytics();

  const location = useLocation();
  const isWorklistPage = location.pathname === '/worklist';
  const postLoadState = usePostLoadState({
    enabled: isWorklistPage,
    context: APOLLO_QUERY_CONTEXT,
  });
  const loadingScreen = useLoadingScreen({ context: APOLLO_QUERY_CONTEXT });
  const showSidebar = useSidebarVisible();

  const { isAuthenticated, logout, login, loginMFA, error } = useAuth({
    context: APOLLO_QUERY_CONTEXT,
  });
  const shouldRenderAuthenticatedShell = isAuthenticated && !isLogoutPathname(location.pathname);
  useMonitorAuthToken();

  const auth = useMemo(
    () => ({
      isAuthenticated,
      logout,
      login,
      loginMFA,
      error,
    }),
    [isAuthenticated, logout, login, loginMFA, error]
  );

  // TODO(fzivolo): enable once we are ready to make the navbar toggleable
  const matchPathWorklist = true; // useRouteMatch(PATH.WORKLIST);

  const { shouldRenderLeftSidebar } = useCheckUserRISPermissions();

  return (
    <LoadingScreenContext.Provider value={loadingScreen}>
      <AuthProvider value={auth}>
        {isWorklistPage && (
          <LoginBackground
            css={`
              position: absolute;
              inset: 0;
              z-index: -1;
              opacity: ${postLoadState && !loadingScreen.showLoadingScreen ? '0' : '1'};
            `}
          />
        )}
        {isAuthlessPathname(location.pathname) &&
          !isAuthlessStaticPathname(location.pathname) &&
          isAuthenticated && <Navigate to={AppRoutePath.WORKLIST} />}
        <AnimatePresence>
          {shouldRenderAuthenticatedShell && postLoadState && (
            <div
              css={`
                z-index: 1;
                max-width: ${shouldRenderLeftSidebar && showSidebar ? SIDENAV_WIDTH : 0}px;
                overflow: hidden;
                transition: max-width 0.3s ease-in;
              `}
            >
              <Suspense>
                <Navbar
                  floating={location.pathname === '/worklist'}
                  toggleable={matchPathWorklist == null}
                />
              </Suspense>
            </div>
          )}
        </AnimatePresence>

        <div css="position: relative; flex: 1; isolation: isolate;">
          <AnimatePresence initial={false}>
            <Suspense
              fallback={
                <>
                  <PWATitleBar />
                  <LinearProgress />
                </>
              }
            >
              <Outlet />
            </Suspense>
          </AnimatePresence>
          {shouldRenderAuthenticatedShell && (
            <>
              <SystemCheck />
              <WorkspaceManagerWrapper />
            </>
          )}
        </div>
      </AuthProvider>
    </LoadingScreenContext.Provider>
  );
}

BackgroundRoute.displayName = 'BackgroundRoute';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(BackgroundRoute);
